import React, { useMemo, useRef } from 'react';
import { styled } from '@withjoy/joykit';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';

import { useMarkThemeArtPainted } from './ThemeStylesProvider';
import { computeFixedElementStyle, computeStaticFixedStyle } from './internal/utils/computeFixedSpriteStyle';
import { buildSpriteImageSource, isAboveTheFold, toImageLayerStyle } from './spriteImage';
import type { ApplicationContext } from './styleApplicator.types';
import type { CSSObject, Sprite } from './themeOutput.types';

/**
 * React renderer for theme art sprites (PRNT-4898) — replaces the legacy applicator's
 * imperative DOM path (createElement / insertBefore / JSS stylesheets / per-sprite resize
 * listeners) for the bundled arm.
 *
 * Each sprite's computedStyleObject comes straight from the theme's style JSON and is
 * CSS-in-JS with nested selectors (`& > *:first-child` carries the art, sometimes `&::after`) —
 * styled-components object interpolation renders it natively, which is everything the legacy
 * JSS pipeline (camelCase/nested/vendor plugins) was doing.
 *
 * The art itself is an `<img>` rather than the JSON's `background-image`: the preload scanner
 * discovers it while the HTML streams (a background waits for CSSOM and layout), and
 * above-the-fold art loads eagerly rather than on approach. It claims no priority (see
 * spriteImage.ts) but carries `srcSet`/`sizes` so the browser picks a rendition for the device.
 *
 * A canvas that is only on screen inside a media range (Brannan's frames from sm4 up, its title
 * banner below) passes that range as `media`, and the art is then wrapped in a `<picture>` whose
 * one `<source>` carries the real candidates under that query, with a blank `<img>` as the
 * fallback. The server picks the canvas from a User-Agent guess, and hydration swaps it when the
 * guess was wrong (a narrow desktop window, a landscape tablet); a hidden frame's `<img>` still
 * downloads, so without the gate a wrong guess cost a full sprite before the swap.
 *
 * Sprites render as direct children of the frame node, first in source order — the same DOM
 * position the legacy applicator's `insertBefore(fragment, firstChild)` produced.
 *
 * Scroll-fixed sprites (fixedPositionWhenScrolling) split two ways. A 'parent-fill' back-layer
 * sprite resolves to a constant window-filling box, so its style is applied during render and it
 * paints straight from the SSR markup — no hidden first frame, no client round trip. Sprites
 * whose geometry depends on the viewport ('original' constraint) still measure the parent frame
 * in useLayoutEffect — before paint, so no pre-position frame ever renders — and stay
 * visibility: hidden until then, which layout-shift tracking exempts.
 */

/**
 * A 1x1 transparent GIF, inline so it costs no request: what a media-gated sprite's `<img>` resolves
 * to when its canvas is off screen (see `ThemeSpritesProps.media`).
 */
const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const SpriteBox = styled.div<{ $spriteStyle: CSSObject; $fixedStyle?: CSSObject }>`
  ${({ $spriteStyle }) => $spriteStyle as never}
  /* The measurement-free viewport-fixed box, applied at render so it is in the SSR markup.
     After $spriteStyle because the legacy applicator set it on the element, overriding the class. */
  ${({ $fixedStyle }) => ($fixedStyle ?? {}) as never}
  /* After the sprite styles: the legacy applicator force-set this by mutation, so it must win. */
  pointer-events: none;
  /* Media-gated art: the JSON's art-layer rules (box, object-fit, object-position) land on the
     <picture>, which is the first child; the <img> inside fills it and inherits the fit. */
  & > picture > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: inherit;
    object-position: inherit;
  }
`;

const isViewportFixed = (sprite: Sprite, context: ApplicationContext): boolean => {
  if (!sprite.fixedPositionWhenScrolling) {
    return false;
  }
  if (sprite.scale.constraint === 'original') {
    return true;
  }
  return sprite.scale.constraint === 'parent-fill' && 'layer' in context && context.layer === 'back';
};

type ThemeSpriteProps = Readonly<{
  sprite: Sprite;
  context: ApplicationContext;
  frameInset?: string;
  media?: string;
}>;

// Memoized: Brannan mounts three canvases and guest pages re-render on scroll, nav and Apollo
// updates, while a sprite's inputs are constant for the life of its `sprite` object (built once in
// useThemeCanvas's useMemo). Rebuilding the nested CSSObject each render handed styled-components
// a fresh identity to re-flatten and re-hash per sprite, per render.
const ThemeSprite = React.memo<ThemeSpriteProps>(({ sprite, context, frameInset, media }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Known at render time; no measurement, so it can be server-rendered and never hidden.
  const staticFixedStyle = useMemo(() => computeStaticFixedStyle(context, sprite, frameInset), [context, sprite, frameInset]);
  const spriteStyle = useMemo(() => toImageLayerStyle(sprite.computedStyleObject), [sprite]);
  const fixed = !staticFixedStyle && isViewportFixed(sprite, context);
  const image = buildSpriteImageSource(sprite);
  const aboveTheFold = isAboveTheFold(sprite, context);
  const scale = sprite.scale as { constraint: string; factor?: number };
  // MUST list every field computeFixedElementStyle reads: the effect below re-runs only when this
  // key changes, so a field added there but not here leaves fixed sprites positioning from stale
  // values on resize — and nothing reports it (exhaustive-deps is only a warning here).
  const geometryKey = [
    sprite.id,
    scale.constraint,
    scale.factor ?? '',
    sprite.asset.width,
    sprite.asset.height,
    sprite.placement.x,
    sprite.placement.y,
    sprite.offset.x,
    sprite.offset.y,
    'component' in context ? context.component : `${context.composition}:${context.layer}`,
  ].join('|');

  // Isomorphic: React warns that useLayoutEffect is a no-op during SSR, and these sprites do
  // server-render. Client behaviour is unchanged — it is still useLayoutEffect in the browser.
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    const parent = element?.parentElement;
    // The view this sprite renders in — never the global `window`. Effects don't run during
    // SSR, but reading the element's own view is also what keeps the geometry correct inside
    // the website-designer preview, where the guest app renders in an iframe.
    const view = element?.ownerDocument?.defaultView;
    if (!fixed || !element || !parent || !view) {
      return;
    }

    const position = () => {
      const styles = computeFixedElementStyle(parent, context, sprite, view);
      Object.entries(styles).forEach(([property, value]) => {
        (element.style as unknown as Record<string, unknown>)[property] = value;
      });
      element.style.visibility = '';
    };

    // Position before first paint — no pre-position frame ever renders, so no layout shift.
    position();

    // Keep viewport-relative geometry in sync; rAF coalesces bursts of resize events.
    let frame: number | undefined;
    const onResize = () => {
      if (frame === undefined) {
        frame = view.requestAnimationFrame(() => {
          frame = undefined;
          position();
        });
      }
    };
    view.addEventListener('resize', onResize, { passive: true });
    return () => {
      view.removeEventListener('resize', onResize);
      if (frame !== undefined) {
        view.cancelAnimationFrame(frame);
      }
    };
    // Keyed on the values the geometry is actually computed from rather than on object
    // identity: the sprite/context props are rebuilt every render, so depending on them
    // directly would tear down and re-add the resize listener continuously — but depending on
    // `fixed` alone left a stale closure when a theme switch reused a sprite id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixed, geometryKey]);

  // A plain <img>, not joykit Box: this carries raw image attributes and no style props.
  // Decorative — the parent is aria-hidden and the legacy sprites had no text alternative.
  // `alt=""` stays literal on each <img> below: jsx-a11y/alt-text cannot see it through a spread.
  const imageAttributes = {
    // `async` everywhere, deliberately. `sync` would remove an art-only flicker on warm loads
    // (the frame paints a beat before the art fills in), but it does so by decoding on the main
    // thread before the frame is presented — and the bundled above-the-fold art runs to
    // 12432x7312 (~364MB of RGBA). The header photo is the LCP element and could not paint
    // until that decode finished: the same hero-vs-art competition the missing `fetchpriority`
    // avoids at fetch time, reintroduced at decode time. `loading="eager"` and the <head>
    // preload already deliver the early fetch, which is the part that matters.
    decoding: 'async',
    draggable: false,
    // Start above-the-fold art now, below-the-fold art on approach. No `fetchpriority`: the
    // header photo is the LCP element and owns the high band — see spriteImage.ts, where the
    // same rule holds for the <head> preload, because one side alone would not hold it.
    loading: aboveTheFold ? 'eager' : 'lazy',
  } as const;

  return (
    <SpriteBox
      ref={ref}
      // Marker for layout CSS (StyledBoxBrannan's z-index layering matches legacy sprites via
      // `[class^='jyse-']` and React sprites via this attribute). A jyse- class can't work
      // here: styled-components appends the passed className last, so it never prefixes the
      // class attribute.
      data-theme-sprite={sprite.id}
      $spriteStyle={spriteStyle}
      $fixedStyle={staticFixedStyle}
      style={fixed ? { visibility: 'hidden' } : undefined}
      aria-hidden={true}
    >
      {media ? (
        // Art direction to nothing: the <source> is the only candidate set, so outside `media`
        // the browser takes the blank fallback and fetches no art. Candidates ride on the source
        // (`srcSet` alone when there is no ladder) so the resolution matches the <img> form and
        // the <head> preload, which carries the same `media`.
        <picture>
          <source
            media={media}
            srcSet={image.srcSet ?? image.src}
            sizes={image.srcSet ? image.sizes : undefined}
          />
          <img
            src={BLANK_IMAGE}
            alt=""
            {...imageAttributes}
          />
        </picture>
      ) : (
        <img
          src={image.src}
          srcSet={image.srcSet}
          sizes={image.srcSet ? image.sizes : undefined}
          alt=""
          {...imageAttributes}
        />
      )}
    </SpriteBox>
  );
});
ThemeSprite.displayName = 'ThemeSprite';

export type ThemeSpritesProps = Readonly<{
  sprites: ReadonlyArray<Sprite>;
  context: ApplicationContext;
  /**
   * The frame's distance from the viewport edge, as a CSS length the browser can resolve on its
   * own (e.g. '0px' for a full-bleed frame, 'max(0px, calc(50% - 512px))' for one centred at a
   * max width). Supplying it lets viewport-fixed sprites position in CSS at render time — so they
   * are visible in the SSR markup — instead of waiting for a layout effect to measure the frame.
   * Omit it when the frame's geometry isn't expressible; those sprites keep the measured path.
   */
  frameInset?: string;
  /**
   * The media query (without `@media`) under which this canvas is on screen — the same string
   * the server's preload link carries for it. When given, a viewport outside it resolves each
   * sprite to a blank image, so a canvas the server rendered from a wrong User-Agent guess costs
   * no download before hydration replaces it. Omit for a canvas that paints at every width.
   */
  media?: string;
}>;

export const ThemeSprites: React.FC<ThemeSpritesProps> = ({ sprites, context, frameInset, media }) => {
  const markPainted = useMarkThemeArtPainted();
  // During render, not in an effect: effects never run in SSR, which is the only place this
  // records anything (on the client it's a no-op). Idempotent across render passes.
  if (sprites.length > 0) {
    markPainted(context);
  }
  return (
    <>
      {sprites.map(sprite => (
        <ThemeSprite
          key={sprite.id}
          sprite={sprite}
          context={context}
          frameInset={frameInset}
          media={media}
        />
      ))}
    </>
  );
};
